import { useCallback, useEffect, useState } from 'react';
import { useTokens } from '../theme.jsx';
import { getSeats, listFundingRequests, submitFundingRequest } from '../services/institutions.js';
import {
  Button, Card, Field, Note, SectionTitle,
} from '../uiKit.jsx';
import { MetricsSkeleton } from '../Skeleton.jsx';
import { notifyError, notifySuccess } from '../toast.js';

const METRICS = [
  ['aiCredits', 'Coffee credits / month'],
  ['aiAvatarMinutes', 'Avatar minutes / month'],
  ['aiVoiceAgentMinutes', 'Voice minutes / month'],
  ['deepResearchPerMonth', 'Deep research runs / month'],
  ['aiChatTokensPerMonth', 'AI chat tokens / month'],
];
// add-job-credits D14: shown only while the seats payload carries
// jobCreditPool (FEATURE_JOB_CREDITS on), so a request made while it is off
// never names jobCredits.
const JOB_CREDITS_METRIC = ['jobCredits', 'Job credits / period'];

const toForm = (limits = {}, metrics = METRICS) => {
  return metrics.reduce((result, [key]) => {
    result[key] = limits[key] === undefined || limits[key] === null ? '' : String(limits[key]);
    return result;
  }, {});
};

export default function EnterpriseUsagePanel({ institutionId, onChange }) {
  const t = useTokens();
  const [values, setValues] = useState(() => { return toForm(); });
  const [defaultLimits, setDefaultLimits] = useState({});
  const [planPresets, setPlanPresets] = useState({});
  const [plan, setPlan] = useState('PRO');
  const [metrics, setMetrics] = useState(METRICS);
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [seatData, requestData] = await Promise.all([
        getSeats(institutionId),
        listFundingRequests(institutionId),
      ]);
      const request = requestData?.requests?.find((item) => {
        return item.type === 'CUSTOM_USAGE' && item.status === 'PENDING';
      }) || null;
      setPlan(request?.payload?.seatPlan || seatData?.seatPlan || 'PRO');
      setPending(request);
      setDefaultLimits(seatData?.effectivePlanLimits || {});
      setPlanPresets(seatData?.planPresets || {});
      const shown = seatData?.jobCreditPool ? [...METRICS, JOB_CREDITS_METRIC] : METRICS;
      setMetrics(shown);
      const savedOverrides = seatData?.entitlementOverrides || {};
      setValues(toForm(request?.payload?.entitlementOverrides
        || (Object.keys(savedOverrides).length > 0 ? savedOverrides : {}), shown));
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const entitlementOverrides = {};
    for (const [key] of metrics) {
      const raw = values[key]?.trim();
      if (!raw) { continue; }
      const value = Number(raw);
      if (!Number.isInteger(value) || value < 0) { return; }
      entitlementOverrides[key] = value;
    }
    setBusy(true);
    try {
      await submitFundingRequest({
        type: 'CUSTOM_USAGE',
        payload: { entitlementOverrides },
      }, institutionId);
      await load();
      onChange?.();
      notifySuccess('Enterprise usage request submitted.');
    } catch (err) {
      notifyError(err?.response?.data?.error || 'Could not submit the Enterprise usage request.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <Card><SectionTitle>Enterprise usage</SectionTitle><MetricsSkeleton count={4} /></Card>;
  }

  return (
    <Card>
      <SectionTitle>Enterprise usage</SectionTitle>
      <p className="mb-4 text-sm" style={{ color: t.muted, fontFamily: t.fonts.body }}>
        {plan === 'ENTERPRISE'
          ? 'Request changes to the contracted allowances applied to every sponsored Enterprise seat.'
          : 'Request a custom allowance package. Approval moves the institution to Enterprise.'}
      </p>
      {pending ? (
        <p className="mb-4 text-sm" role="status" style={{ color: t.muted, fontFamily: t.fonts.body }}>
          An Enterprise usage request is pending. Updating this form replaces the pending request.
        </p>
      ) : null}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="mr-1 text-xs" style={{ color: t.muted, fontFamily: t.fonts.mono }}>
          Autofill allowances:
        </span>
        {['PRO', 'PREMIUM'].map((preset) => {
          return (
            <Button
              key={preset}
              label={preset === 'PRO' ? 'Pro' : 'Premium'}
              variant="secondary"
              small
              onClick={() => { setValues(toForm(planPresets[preset], metrics)); }}
              disabled={busy || !planPresets[preset]}
            />
          );
        })}
      </div>
      <form
        onSubmit={(event) => { event.preventDefault(); if (!busy) { submit(); } }}
        className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
      >
        {metrics.map(([key, label]) => {
          return (
            <label key={key} className="text-xs" style={{ color: t.muted, fontFamily: t.fonts.mono }}>
              <span className="mb-1 block">{label}</span>
              <Field
                value={values[key]}
                onChangeText={(value) => { setValues((current) => { return { ...current, [key]: value }; }); }}
                inputMode="numeric"
                placeholder={defaultLimits[key] === undefined || defaultLimits[key] === null
                  ? 'No default'
                  : String(defaultLimits[key])}
                className="!mb-0"
              />
            </label>
          );
        })}
        <div className="flex items-end">
          <Button
            type="submit"
            label={busy ? 'Submitting…' : (pending ? 'Update request' : 'Request Enterprise usage')}
            disabled={busy}
          />
        </div>
      </form>
      <Note className="mt-3">Only admins with Billing permission can submit this request.</Note>
    </Card>
  );
}
